document.addEventListener('alpine:init', () => {
    Alpine.data('cartDrawerItem', (item, quantityDisabled = false) => ({
        item,
        // A grouped row covers several real lines; show the group's total.
        qty: item?._groupQuantity ?? item?.quantity,
        quantityDisabled: Boolean(quantityDisabled),
        isUpdating: false,
        _pendingQty: null,
        _debounceTimer: null,

        /**
         * Initializes the cart drawer item.
         * @returns {void}
         */
        init() {
            // Re-sync this line from the displayed cart whenever it changes
            // from anything other than our own in-flight request
            // (GWP gift add/remove, add-to-cart from a product page, etc.).
            // Watching `displayData` keeps the bound item consistent with
            // what is currently rendered (the snapshot during pending ops).
            this.$watch('$store.cart.displayData', () => {
                if (this.isUpdating) return;
                this.syncFromStore();
            });
        },

        /**
         * Every real cart line key this row stands for. A row is usually one
         * line, but `displayItems` collapses lines that Shopify split apart
         * (same variant + selling plan) into one row covering several keys.
         * @returns {string[]}
         */
        get groupKeys() {
            const keys = this.item?._groupKeys;
            return Array.isArray(keys) && keys.length ? keys : [this.item?.key].filter(Boolean);
        },

        /**
         * True when this line is a paid subscription purchase.
         * @returns {boolean}
         */
        get isSubscriptionLine() {
            return Alpine.store('cart').isSubscriptionLine(this.item);
        },

        /**
         * Max qty allowed on this line for the subscription product cap.
         * @returns {number|null}
         */
        get subscriptionMaxQty() {
            if (!this.isSubscriptionLine) return null;
            return Alpine.store('cart').getSubscriptionMaxQtyForLine(this.item);
        },

        /**
         * True when + / higher manual input should be blocked.
         * @returns {boolean}
         */
        get isSubscriptionQtyMaxed() {
            if (!this.isSubscriptionLine) return false;
            const max = this.subscriptionMaxQty;
            return Number.isFinite(max) && this.qty >= max;
        },

        /**
         * Server quantity currently shown for this row.
         * @returns {number}
         */
        get serverQty() {
            return this.item._groupQuantity ?? this.item.quantity;
        },

        /**
         * Re-pulls this line from the cart store and resets item + qty.
         * Safe to call after our own request resolves or when the cart
         * changes externally.
         * @returns {void}
         */
        syncFromStore() {
            const updated = Alpine.store('cart').displayItems.find((i) => i.key === this.item.key);
            if (!updated) return; // line was removed; x-for will unmount this component
            this.item = updated;
            this.qty = updated._groupQuantity ?? updated.quantity;
        },

        /**
         * Normalizes quantity values from UI/input.
         * @param {number|string} qty - The quantity to normalize.
         * @returns {number}
         */
        normalizeQty(qty) {
            const parsedQty = Number(qty);

            if (!Number.isFinite(parsedQty)) return 1;

            let result = Math.max(1, Math.floor(parsedQty));

            if (this.isSubscriptionLine) {
                const max = this.subscriptionMaxQty;
                if (Number.isFinite(max)) {
                    result = Math.min(result, Math.max(1, max));
                }
            }

            return result;
        },

        /**
         * Clears any pending debounced quantity commit.
         * @returns {void}
         */
        clearDebounce() {
            if (this._debounceTimer != null) {
                clearTimeout(this._debounceTimer);
                this._debounceTimer = null;
            }
        },

        /**
         * Clamps local qty and schedules a cart update (debounced).
         * Used by +/−, controlled arrow keys, typing, and spinner.
         * @param {number|string} qty - Desired quantity.
         * @returns {void}
         */
        scheduleQtyUpdate(qty) {
            if (this.quantityDisabled) return;

            const next = this.normalizeQty(qty);
            this.qty = next;

            if (next === this.serverQty && !this.isUpdating) return;

            // One in-flight request at a time; keep only the latest intent.
            if (this.isUpdating) {
                this._pendingQty = next;
                return;
            }

            this.clearDebounce();
            this._debounceTimer = setTimeout(() => {
                this._debounceTimer = null;
                this.sendRequest(next);
            }, 250);
        },

        /**
         * Commits quantity immediately (blur / Enter). Cancels debounce so we
         * do not double-fire after a trailing timer.
         * @param {number|string} [qty]
         * @returns {void}
         */
        commitQuantity(qty = this.qty) {
            if (this.quantityDisabled) return;

            const next = this.normalizeQty(qty);
            this.qty = next;
            this.clearDebounce();

            if (next === this.serverQty && !this.isUpdating) return;
            this.sendRequest(next);
        },

        /**
         * Clamps whatever x-model wrote (arrows/spinner can exceed HTML max)
         * and schedules a cart update.
         * @param {number|string} qty
         * @returns {void}
         */
        onQtyInput(qty) {
            this.scheduleQtyUpdate(qty);
        },

        /**
         * Increases quantity by one.
         * @returns {void}
         */
        increaseQuantity() {
            if (this.quantityDisabled || this.isSubscriptionQtyMaxed) return;
            this.scheduleQtyUpdate(this.qty + 1);
        },

        /**
         * Decreases quantity by one.
         * @returns {void}
         */
        decreaseQuantity() {
            if (this.quantityDisabled) return;
            this.scheduleQtyUpdate(this.qty - 1);
        },

        /**
         * Removes the item from the cart.
         * @returns {void}
         */
        removeItem() {
            this.clearDebounce();
            this.sendRequest(0);
        },

        /**
         * Sends the request to update the quantity of the item in the cart.
         * Serializes concurrent calls: later intents are queued as `_pendingQty`
         * and flushed after the in-flight request finishes.
         * @param {number} qty - The quantity to send.
         * @returns {Promise}
         */
        sendRequest(qty = this.qty) {
            const target = qty === 0 ? 0 : this.normalizeQty(qty);

            if (this.isUpdating) {
                this._pendingQty = target;
                return Promise.resolve();
            }

            if (target !== 0 && target === this.serverQty) {
                this.qty = this.serverQty;
                return Promise.resolve();
            }

            if (target !== 0) this.qty = target;

            this.isUpdating = true;
            this._pendingQty = null;

            return Alpine.store('cart')
                .updateGroupQty(this.groupKeys, target)
                .then(() => {
                    this.syncFromStore();
                })
                .finally(() => {
                    this.isUpdating = false;

                    if (this._pendingQty === null) return;

                    const pending = this._pendingQty;
                    this._pendingQty = null;
                    const synced = this.serverQty;

                    if (pending !== synced) {
                        this.sendRequest(pending);
                    } else {
                        this.qty = synced;
                    }
                });
        },
    }));
});
